/**
 * Detection Limits Configuration for Frontend
 * Each element has its detection limit and unit
 * Values below detection limit should display as the limit value
 */

export interface ElementDetectionLimit {
  limit: number;
  unit: 'g/t' | 'ppm' | '%';
  displayBelowLimit: 'limit' | 'less_than'; // 'limit' shows the limit value, 'less_than' shows <limit
  roundingThreshold?: number; // Values between 0 and this threshold will be rounded to limit
}

export const ELEMENT_DETECTION_LIMITS: Record<string, ElementDetectionLimit> = {
  // Gold and Silver - g/t (grams per tonne)
  'Au': { limit: 0.05, unit: 'g/t', displayBelowLimit: 'less_than', roundingThreshold: 0.04 },
  'Ag': { limit: 1, unit: 'g/t', displayBelowLimit: 'less_than', roundingThreshold: 0.8 },
  
  // Trace elements - ppm (parts per million)
  'As': { limit: 1, unit: 'ppm', displayBelowLimit: 'less_than', roundingThreshold: 0.8 },
  'Mo': { limit: 1, unit: 'ppm', displayBelowLimit: 'less_than', roundingThreshold: 0.8 },
  'Sn': { limit: 1, unit: 'ppm', displayBelowLimit: 'less_than', roundingThreshold: 0.8 },
  
  // Major elements - % (percentage)
  'Cu': { limit: 0.01, unit: '%', displayBelowLimit: 'less_than', roundingThreshold: 0.008 },
  'Fe': { limit: 0.01, unit: '%', displayBelowLimit: 'less_than', roundingThreshold: 0.008 },
  'Pb': { limit: 0.01, unit: '%', displayBelowLimit: 'less_than', roundingThreshold: 0.008 },
  'Zn': { limit: 0.01, unit: '%', displayBelowLimit: 'less_than', roundingThreshold: 0.008 },
};

/**
 * Apply detection limit to a value for storage
 * @param element The element symbol (e.g., 'Au', 'Cu')
 * @param value The measured value
 * @returns The value processed according to detection limit rules
 */
export function applyDetectionLimit(element: string, value: number | null | undefined): number {
  const config = ELEMENT_DETECTION_LIMITS[element];
  if (!config) {
    return value ?? 0; // No detection limit configured for this element
  }
  
  const numValue = value ?? 0;
  
  // If value is negative, store as 0 (will be displayed as <limit)
  if (numValue < 0) {
    return 0;
  }
  
  // If value is between 0 and roundingThreshold, store as the limit value
  if (config.roundingThreshold && numValue > 0 && numValue <= config.roundingThreshold) {
    return config.limit;
  }
  
  // Otherwise store the actual value
  return numValue;
}

/**
 * Format value with detection limit consideration for display
 * @param element The element symbol
 * @param value The measured value
 * @returns Formatted string for display
 */
export function formatWithDetectionLimit(element: string, value: number | null | undefined): string {
  const config = ELEMENT_DETECTION_LIMITS[element];
  if (!config) {
    return value?.toString() ?? '0';
  }
  
  const numValue = value ?? 0;
  
  // If value is 0 or negative, display as <limit
  if (numValue <= 0) {
    return `<${config.limit}`;
  }
  
  // If value is below detection limit, display as <limit
  if (numValue < config.limit) {
    return `<${config.limit}`;
  }
  
  // Format based on unit for values above detection limit
  if (config.unit === '%') {
    return numValue.toFixed(2);
  } else if (config.unit === 'g/t') {
    return numValue.toFixed(2);
  } else {
    return numValue.toFixed(0);
  }
}

/**
 * Get the unit for an element
 * @param element The element symbol
 * @returns The unit string
 */
export function getElementUnit(element: string): string {
  const config = ELEMENT_DETECTION_LIMITS[element];
  return config?.unit || 'ppm';
}

/**
 * Check if a value is below detection limit
 * @param element The element symbol
 * @param value The measured value
 * @returns True if below detection limit
 */
export function isBelowDetectionLimit(element: string, value: number | null | undefined): boolean {
  const config = ELEMENT_DETECTION_LIMITS[element];
  if (!config) return false;
  
  return value === null || value === undefined || value < config.limit;
}